import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';

export default function Step4() {
  const navigate = useNavigate();
  const [periodicity, setPeriodicity] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Récupérer les valeurs sauvegardées si existantes
    const savedPeriodicity = localStorage.getItem('periodicity');
    const savedEffectiveDate = localStorage.getItem('effectiveDate');
    if (savedPeriodicity) setPeriodicity(savedPeriodicity);
    if (savedEffectiveDate) setEffectiveDate(savedEffectiveDate);
  }, []);

  const handleContinue = (e) => {
    e.preventDefault();
    let newErrors = {};
    
    if (!periodicity) {
      newErrors.periodicity = 'Veuillez sélectionner une périodicité.';
    }
    if (!effectiveDate) {
      newErrors.effectiveDate = 'Veuillez saisir la date d\'effet.';
    } else {
      // Vérifier que la date d'effet est supérieure ou égale à aujourd'hui
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(effectiveDate);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.effectiveDate = 'La date d\'effet doit être supérieure ou égale à la date du jour.';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    localStorage.setItem('periodicity', periodicity);
    localStorage.setItem('effectiveDate', effectiveDate);
    navigate('/step-5');
  };

  // Déterminer le lien retour en fonction du type d'assurance
  const assuredType = localStorage.getItem('assuredType');
  const SOLO_TYPE_UUID = '019beade-d8aa-71da-85e4-afa966b97457';
  const backLink = assuredType === SOLO_TYPE_UUID ? '/step-2' : '/step-3';

  return (
    <div className="subscribe py-10 lg:py-16 bg-[#e4eef9]">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-center text-4xl font-bold">Formulaire de souscription</h1>
        <div className="mt-10">
          <ProgressBar currentStep={4} />
          <div className="mt-8 bg-white p-6 lg:p-10 rounded-xl shadow-sm">
            <h2 className="text-xl font-bold text-secondary mb-6">4. Périodicité et date d'effet</h2>
            <form className="space-y-6">
              <div>
                <label className="block font-bold mb-4">À quelle périodicité voulez-vous payer votre prime ?</label>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition ${periodicity === 'annuelle' ? 'bg-primary/10 border-2 border-primary' : 'border-2 border-gray-200 hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="periodicity"
                      value="annuelle"
                      checked={periodicity === 'annuelle'}
                      onChange={(e) => {
                        setPeriodicity(e.target.value);
                        if (errors.periodicity) setErrors({...errors, periodicity: ''});
                      }}
                      className="h-5 w-5"
                    />
                    <div>
                      <span className="font-bold">Annuelle (unique)</span>
                      <p className="text-sm text-gray-600">Paiement en une seule fois pour toute l'année</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition ${periodicity === 'mensuelle' ? 'bg-primary/10 border-2 border-primary' : 'border-2 border-gray-200 hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="periodicity"
                      value="mensuelle"
                      checked={periodicity === 'mensuelle'}
                      onChange={(e) => {
                        setPeriodicity(e.target.value);
                        if (errors.periodicity) setErrors({...errors, periodicity: ''});
                      }}
                      className="h-5 w-5"
                    />
                    <div>
                      <span className="font-bold">Mensuelle</span>
                      <p className="text-sm text-gray-600">6 mois maximum sans interruption</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition ${periodicity === 'trimestrielle' ? 'bg-primary/10 border-2 border-primary' : 'border-2 border-gray-200 hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="periodicity"
                      value="trimestrielle"
                      checked={periodicity === 'trimestrielle'}
                      onChange={(e) => {
                        setPeriodicity(e.target.value);
                        if (errors.periodicity) setErrors({...errors, periodicity: ''});
                      }}
                      className="h-5 w-5"
                    />
                    <div>
                      <span className="font-bold">Trimestrielle</span>
                      <p className="text-sm text-gray-600">2 fois maximum – le premier mois et le 4e mois</p>
                    </div>
                  </label>
                </div>
                {errors.periodicity && <p className="text-red-500 text-sm mt-2">{errors.periodicity}</p>}
              </div>
              
              <div>
                <label className="block font-bold mb-2">Quelle est la date d'effet du contrat ?</label>
                <p className="text-sm text-gray-600 mb-3 p-3 bg-blue-50 rounded-lg">
                  <strong>Note :</strong> La prise d'effet est égale à la date de règlement de la prime.
                </p>
                <input
                  type="date"
                  value={effectiveDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    setEffectiveDate(e.target.value);
                    if (errors.effectiveDate) setErrors({...errors, effectiveDate: ''});
                  }}
                  className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                />
                {errors.effectiveDate && <p className="text-red-500 text-sm mt-2">{errors.effectiveDate}</p>}
              </div>
            </form>
            
            <div className="text-center mt-8 flex justify-center gap-4 pt-6 border-t">
              <Link
                to={backLink}
                className="rounded-full border border-gray-300 px-6 py-3 text-gray-700 flex items-center gap-2"
              >
                ← Retour
              </Link>
              <button
                onClick={handleContinue}
                className="rounded-full bg-primary px-8 py-3 text-secondary font-medium flex items-center gap-2"
              >
                Continuer →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
